import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { bookingService, toCamelCase, sendAdminEmail } from '../../services/bookingService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Image from '../../components/AppImage';
import { formatNumber } from '../../utils/formatNumber';
import {
  getWorkloadLabelAr,
  getDeliveredPhotosLabelAr,
  getShootingMethodLabelAr,
  getPhotographyTypeLabelAr,
  getProductPhysicalSizeLabelAr,
  getShootingPlaceTypeLabelAr,
} from '../../utils/bookingLabels';
import { getCityLabel } from '../../utils/cityNormalization';

// ⭐ مكوّن التقييم بالنجوم
const StarRating = ({ rating, onRate, size = 30, interactive = true }) => {
  const [hoverRating, setHoverRating] = useState(0);
  return (
    <div className="flex items-center gap-1" dir="ltr">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => interactive && onRate?.(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
          disabled={!interactive}
        >
          <Icon
            name="Star"
            size={size}
            className={`transition-colors duration-150 ${
              star <= (hoverRating || rating)
                ? 'text-amber-400 fill-amber-400'
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const formatDurationAr = (duration) => {
  const n = Number(duration);
  if (!duration && duration !== 0) return 'غير محدد';
  if (n === 1) return 'ساعة';
  if (n === 2) return 'ساعتان';
  if (n >= 3 && n <= 10) return `${n} ساعات`;
  return `${n} ساعة`;
};

// Helper function to format time to Arabic 12-hour format (ص/م)
const formatTimeToArabic = (time) => {
  if (!time) return 'غير محدد';
  
  // If already formatted, return as is
  if (typeof time === 'string' && (time.includes('ص') || time.includes('م'))) {
    return time;
  }
  
  // Parse time (format: HH:MM or HH:MM:SS)
  const timeStr = String(time);
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours, 10);
  const min = minutes || '00';
  
  if (isNaN(hour)) return time;
  
  // Format to 12-hour with ص/م
  if (hour === 0) {
    return `12:${min} ص`;
  } else if (hour < 12) {
    return `${hour}:${min} ص`;
  } else if (hour === 12) {
    return `12:${min} م`;
  } else {
    const displayHour = hour - 12;
    return `${displayHour}:${min} م`;
  }
};

// عرض اسم المصور موحّد مع الحجز المباشر (من user_profiles أو photographer_profiles)
const getPhotographerDisplayName = (booking) =>
  booking?.photographer?.full_name ||
  booking?.photographer?.fullName ||
  booking?.photographer?.business_name ||
  booking?.photographer?.businessName ||
  'مصور';

// ── مكوّن زر إبلاغ غياب المصور مع عداد تنازلي ────────────────────────────────
const NO_SHOW_DELAY_MS = 60 * 60 * 1000; // 60 دقيقة

const NoShowButton = ({ booking, alreadyReported, reporting, onReport }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const rawDate = booking.booking_date || booking.bookingDate;
  const rawTime = booking.booking_time || booking.bookingTime || '00:00';
  const sessionStart = rawDate ? (() => {
    const d = new Date(rawDate);
    const [h, m] = String(rawTime).split(':').map(Number);
    d.setHours(h || 0, m || 0, 0, 0);
    return d;
  })() : null;

  // الجلسة لم تبدأ بعد — لا نُظهر الزر
  if (!sessionStart || now < sessionStart) return null;

  const threshold = new Date(sessionStart.getTime() + NO_SHOW_DELAY_MS);
  const isActive  = now >= threshold;

  // بعد الإبلاغ
  if (alreadyReported) {
    return (
      <div className="flex items-start gap-2 px-3 py-3 bg-green-50 border border-green-200 rounded-xl" dir="rtl">
        <span className="text-green-600 text-base mt-0.5">✅</span>
        <div>
          <p className="text-xs font-bold text-green-700" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>تم إبلاغ الفريق</p>
          <p className="text-xs text-green-600 mt-0.5" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>سنتواصل معك خلال الساعات القادمة لحل المشكلة</p>
        </div>
      </div>
    );
  }

  // زر نشط بعد 60 دقيقة
  if (isActive) {
    return (
      <div className="w-full space-y-2" dir="rtl">
        <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
          <span className="text-amber-500 text-sm mt-0.5">⚠️</span>
          <p className="text-xs text-amber-700 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            حان موعد جلستك. إذا لم يحضر المصور أبلغنا وسنعيد لك المبلغ كاملاً.
          </p>
        </div>
        <button
          disabled={reporting}
          onClick={onReport}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-3 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-60 text-white text-xs font-bold rounded-xl transition-colors"
          style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
        >
          {reporting
            ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <span>🚨</span>}
          <span>إبلاغ عن غياب المصور</span>
        </button>
      </div>
    );
  }

  // زر معطّل مع عداد تنازلي
  const remainingMs   = threshold - now;
  const remainingMins = Math.floor(remainingMs / 60000);
  const remainingSecs = Math.floor((remainingMs % 60000) / 1000);

  return (
    <button
      disabled
      className="w-full flex items-center justify-center gap-2 py-2.5 px-3 bg-slate-200 text-slate-400 text-xs font-bold rounded-xl cursor-not-allowed select-none"
      style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
      title="سيصبح هذا الزر نشطاً بعد 60 دقيقة من موعد الجلسة"
    >
      <span>🚨</span>
      <span>إبلاغ عن غياب المصور</span>
      <span className="font-mono bg-slate-300 text-slate-500 px-1.5 py-0.5 rounded text-[10px]">
        {remainingMins}:{String(remainingSecs).padStart(2, '0')}
      </span>
    </button>
  );
};

const Bookings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('all');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const { user } = useAuth();
  const realtimeRef = useRef(null);

  // state التقييم
  const [reviewBookingId, setReviewBookingId] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);

  // state تحميل الملفات
  const [downloadingFilesId, setDownloadingFilesId] = useState(null);

  // state إبلاغ غياب المصور
  // يقرأ من localStorage عند التحميل لمنع التكرار حتى بعد تحديث الصفحة
  const [noShowReportedIds, setNoShowReportedIds] = useState(() => {
    try {
      const stored = localStorage.getItem('noshow_reported');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });
  const [reportingNoShowId, setReportingNoShowId] = useState(null);
  // قفل متزامن لمنع الضغط المتكرر قبل أي re-render
  const noShowLockRef = useRef(new Set());
  const [deliveryUrlModal, setDeliveryUrlModal] = useState(null);

  // state طلب الإلغاء بعد الدفع
  const [cancelRequestBooking, setCancelRequestBooking] = useState(null); // { id, totalPrice, bookingDate, bookingTime }
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelPreview, setCancelPreview] = useState(null); // { feePct, refundPct, feeAmount, refundAmount }

  // state استرداد المبلغ بعد انسحاب المصور
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundDoneId, setRefundDoneId] = useState(null); // bookingId that has been submitted

  useEffect(() => {
    loadBookings();
    const bookingId = searchParams.get('bookingId');
    if (bookingId) {
      loadBookingDetails(bookingId);
    }
  }, [searchParams]);

  // Realtime: تحديث فوري عند تغيّر أي حجز (مثل وصول session_pin)
  useEffect(() => {
    if (!user?.id) return;

    realtimeRef.current = supabase
      .channel(`customer_bookings_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bookings',
          filter: `customer_id=eq.${user.id}`,
        },
        (payload) => {
          // Merge both snake_case (for direct field reads) and camelCase (for getMyBookings compat)
          const updated = toCamelCase(payload.new);
          setBookings((prev) =>
            prev.map((b) =>
              b.id === updated.id ? { ...b, ...payload.new, ...updated } : b
            )
          );
        }
      )
      .subscribe();

    return () => {
      realtimeRef.current?.unsubscribe();
    };
  }, [user?.id]);

  // ─── دوال التقييم ───────────────────────────────────────────────────────────
  const openReview = async (booking) => {
    setReviewBookingId(booking.id);
    setReviewRating(0);
    setReviewComment('');
    setExistingReview(null);
    setLoadingReview(true);
    try {
      const { data } = await supabase
        .from('reviews')
        .select('*')
        .eq('booking_id', booking.id)
        .maybeSingle();
      if (data) {
        setExistingReview(data);
        setReviewRating(data.rating || 0);
        setReviewComment(data.comment || '');
      }
    } catch (e) {
      console.error('Failed to load review:', e);
    } finally {
      setLoadingReview(false);
    }
  };

  const closeReview = () => {
    setReviewBookingId(null);
    setExistingReview(null);
    setReviewRating(0);
    setReviewComment('');
  };

  const handleSubmitReview = async (booking) => {
    if (!reviewRating || existingReview) return;
    setSubmittingReview(true);
    try {
      const { error } = await supabase.from('reviews').insert({
        booking_id: booking.id,
        photographer_id: booking.photographerId || booking.photographer_id,
        customer_id: user.id,
        rating: reviewRating,
        comment: reviewComment.trim() || null,
      });
      if (error) throw error;
      // أعد تحميل التقييم بعد الإرسال لعرضه بوضع القراءة
      const { data } = await supabase
        .from('reviews')
        .select('*')
        .eq('booking_id', booking.id)
        .maybeSingle();
      setExistingReview(data);
    } catch (e) {
      console.error('Failed to submit review:', e);
    } finally {
      setSubmittingReview(false);
    }
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      const data = await bookingService.getMyBookings('customer');
      setBookings(data || []);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBookingDetails = async (bookingId) => {
    try {
      const booking = await bookingService.getBooking(bookingId);
      setSelectedBooking(booking);
      setShowDetails(true);
    } catch (error) {
      console.error('Failed to load booking details:', error);
    }
  };

  // ─── إبلاغ عن غياب المصور ───────────────────────────────────────────────────
  const handleReportNoShow = async (booking) => {
    const bookingId = booking.id;

    // قفل متزامن بـ useRef — يمنع التكرار حتى قبل أي re-render
    if (
      noShowLockRef.current.has(bookingId) ||
      booking.no_show_reported_at ||
      noShowReportedIds.has(bookingId)
    ) return;
    noShowLockRef.current.add(bookingId);

    setReportingNoShowId(bookingId);
    try {
      const bookingShortId = String(bookingId).slice(0, 8).toUpperCase();
      const { data: { user: cu } } = await supabase.auth.getUser();

      // إنشاء تذكرة الدعم — يجب استخدام user_type و description لأن الـ trigger يقرأهما
      await supabase.from('support_tickets').insert({
        user_id: cu?.id,
        user_type: 'customer',
        subject: `غياب المصور — حجز #${bookingShortId}`,
        description: `لم يحضر المصور في موعد الجلسة المحددة.\n\nرقم الحجز: ${bookingShortId}\nتاريخ الجلسة: ${booking.booking_date || booking.bookingDate}\nوقت الجلسة: ${booking.booking_time || booking.bookingTime || '—'}\n\nأرجو مراجعة الحجز واتخاذ الإجراء المناسب.`,
        category: 'booking',
        status: 'open',
        priority: 'high',
      });

      // تسجيل وقت الإبلاغ في DB — غير مشروط بالنجاح (العمود قد لا يكون موجوداً بعد)
      supabase.from('bookings')
        .update({ no_show_reported_at: new Date().toISOString() })
        .eq('id', bookingId)
        .then(() => {}).catch(() => {});

      // حفظ في localStorage → يمنع التكرار حتى بعد تحديث الصفحة
      setNoShowReportedIds(prev => {
        const next = new Set([...prev, bookingId]);
        try { localStorage.setItem('noshow_reported', JSON.stringify([...next])); } catch {}
        return next;
      });
    } catch (err) {
      console.error('handleReportNoShow:', err);
      // أفرج عن القفل فقط عند فشل العملية الأساسية (تذكرة الدعم)
      noShowLockRef.current.delete(bookingId);
      alert('حدث خطأ، يرجى المحاولة مرة أخرى.');
    } finally {
      setReportingNoShowId(null);
    }
  };

  // ─── تحميل الملفات المسلّمة ──────────────────────────────────────────────────
  const handleDownloadFiles = async (bookingId) => {
    setDownloadingFilesId(bookingId);
    try {
      const { data: delivery, error: delErr } = await supabase
        .from('booking_deliveries')
        .select('id')
        .eq('booking_id', bookingId)
        .maybeSingle();

      if (delErr || !delivery) {
        alert('لا توجد ملفات مسلّمة لهذا الحجز بعد.');
        return;
      }

      const { data: result, error: fnError } = await supabase.functions.invoke(
        'create-delivery-link',
        { body: { delivery_id: delivery.id } }
      );

      if (fnError || !result?.ok || !result?.url) {
        const msg = result?.message || fnError?.message || 'فشل إنشاء رابط التحميل';
        alert(msg);
        return;
      }

      setDeliveryUrlModal(result.url);
    } catch (err) {
      console.error('handleDownloadFiles:', err);
      alert(err?.message || 'حدث خطأ غير متوقع');
    } finally {
      setDownloadingFilesId(null);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذا الحجز؟')) return;

    try {
      setCancelling(true);
      await bookingService.updateBookingStatus(bookingId, 'cancelled_by_customer');
      await loadBookings();
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking({ ...selectedBooking, status: 'cancelled_by_customer' });
      }
      // Show success message
      alert('تم إلغاء الحجز بنجاح');
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      const errorMessage = error?.message || 'فشل إلغاء الحجز. يرجى المحاولة مرة أخرى.';
      alert(errorMessage);
    } finally {
      setCancelling(false);
    }
  };

  // حساب الرسوم محلياً لعرضها في الـ modal قبل الإرسال
  const computeCancelFee = (bookingDate, bookingTime, totalPrice) => {
    const feePct = bookingService.calculateCancellationFee(bookingDate, bookingTime, 'customer');
    const feeAmount = Math.round((totalPrice * feePct) / 100);
    return { feePct, refundPct: 100 - feePct, feeAmount, refundAmount: totalPrice - feeAmount };
  };

  const openCancelRequest = (booking) => {
    const price = Number(
      booking.finalPriceSar ??
      booking.final_price_sar ??
      booking.finalPrice ??
      booking.final_price ??
      booking.price ??
      0
    );
    const preview = computeCancelFee(
      booking.bookingDate || booking.booking_date,
      booking.bookingTime || booking.booking_time,
      price,
    );
    setCancelPreview(preview);
    setCancelReason('');
    setCancelRequestBooking(booking);
  };

  const handleSubmitCancelRequest = async () => {
    if (!cancelRequestBooking) return;
    setCancelSubmitting(true);
    try {
      await bookingService.requestCancellation(cancelRequestBooking.id, 'customer', cancelReason);
      setCancelRequestBooking(null);
      await loadBookings();
    } catch (err) {
      alert(err?.message || 'فشل طلب الإلغاء، يرجى المحاولة مرة أخرى.');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleRequestRefund = async (bookingId) => {
    if (!window.confirm('هل تريد تقديم طلب استرداد المبلغ؟ ستُراجعه الإدارة وتتواصل معك.')) return;
    setRefundSubmitting(true);
    try {
      await bookingService.requestRefund(bookingId);
      setRefundDoneId(bookingId);
      await loadBookings();
    } catch (err) {
      alert(err?.message || 'فشل طلب الاسترداد، يرجى المحاولة مرة أخرى.');
    } finally {
      setRefundSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_photographer_confirmation: { label: 'في انتظار تأكيد المصور', color: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-300' },
      awaiting_payment: { label: 'في انتظار الدفع', color: 'text-blue-700', bg: 'bg-blue-100', border: 'border-blue-300' },
      paid_confirmed: { label: 'مؤكد ومدفوع', color: 'text-green-700', bg: 'bg-green-100', border: 'border-green-300' },
      session_started: { label: '🎬 الجلسة جارية', color: 'text-purple-700', bg: 'bg-purple-100', border: 'border-purple-300' },
      awaiting_delivery: { label: 'المصور يجهّز الصور', color: 'text-orange-700', bg: 'bg-orange-100', border: 'border-orange-300' },
      completed: { label: '📸 صورك جاهزة!', color: 'text-emerald-700', bg: 'bg-emerald-100', border: 'border-emerald-300' },
      declined: { label: 'مرفوض', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' },
      expired: { label: 'منتهي الصلاحية', color: 'text-slate-700', bg: 'bg-slate-100', border: 'border-slate-300' },
      cancellation_requested: { label: '⏳ إلغاء قيد المراجعة', color: 'text-orange-700', bg: 'bg-orange-100', border: 'border-orange-300' },
      cancelled_by_customer: { label: 'ملغي من العميل', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' },
      cancelled_by_photographer: { label: 'ملغي من المصور', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' },
      refunded: { label: '✅ تم الاسترداد', color: 'text-green-700', bg: 'bg-green-100', border: 'border-green-300' },
      cancelled_by_admin: { label: 'ملغي من المشرف', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' },
      // Legacy statuses for backward compatibility
      pending: { label: 'قيد الانتظار', color: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-300' },
      confirmed: { label: 'مؤكد', color: 'text-green-700', bg: 'bg-green-100', border: 'border-green-300' },
      cancelled: { label: 'ملغي', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' },
    };

    const config = statusConfig[status] || statusConfig.pending_photographer_confirmation;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${config.color} ${config.bg} ${config.border}`}>
        {config.label}
      </span>
    );
  };

  const filteredBookings = bookings.filter((booking) => {
    if (activeTab === 'all') return true;
    
    // Map new statuses to tabs
    const status = booking.status;
    if (activeTab === 'pending') {
      return status === 'pending_photographer_confirmation' || status === 'pending';
    }
    if (activeTab === 'awaiting_payment') {
      return status === 'awaiting_payment';
    }
    if (activeTab === 'confirmed') {
      return status === 'paid_confirmed' || status === 'session_started' || status === 'awaiting_delivery' || status === 'confirmed';
    }
    if (activeTab === 'completed') {
      return status === 'completed';
    }
    if (activeTab === 'cancelled') {
      return status === 'cancelled_by_customer' || status === 'cancelled_by_admin' || status === 'cancelled' || status === 'declined' || status === 'expired' || status === 'cancelled_by_photographer' || status === 'cancellation_requested';
    }
    
    return booking.status === activeTab;
  });

  const tabs = [
    { id: 'all', label: 'الكل', count: bookings.length },
    { id: 'pending', label: 'في انتظار التأكيد', count: bookings.filter(b => b.status === 'pending_photographer_confirmation' || b.status === 'pending').length },
    { id: 'awaiting_payment', label: 'في انتظار الدفع', count: bookings.filter(b => b.status === 'awaiting_payment').length },
    { id: 'confirmed', label: 'مؤكد', count: bookings.filter(b => ['paid_confirmed', 'session_started', 'awaiting_delivery', 'confirmed'].includes(b.status)).length },
    { id: 'completed', label: '📸 صور جاهزة', count: bookings.filter(b => b.status === 'completed').length },
    { id: 'cancelled', label: 'ملغي/منتهي', count: bookings.filter(b => ['cancelled_by_customer', 'cancelled_by_admin', 'cancelled', 'declined', 'expired', 'cancelled_by_photographer', 'cancellation_requested'].includes(b.status)).length },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-28">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">حجوزاتي</h1>
          <p className="text-slate-600">إدارة جميع حجوزاتك وطلباتك</p>
        </div>
        <Button
          variant="default"
          onClick={() => navigate('/booking-wizard')}
          iconName="Plus"
          iconPosition="right"
        >
          حجز جديد
        </Button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-2">
        <div className="flex gap-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-button text-button-foreground'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-slate-200'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Bookings List */}
      {filteredBookings.length > 0 ? (
        <div className="space-y-4">
          {filteredBookings.map((booking) => (
            <div
              key={booking.id}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-6">
                {/* Photographer Image */}
                <div className="flex-shrink-0">
                  <Image
                    src={
                      booking.photographer?.avatar_url ||
                      booking.photographer?.avatarUrl ||
                      'https://img.rocket.new/generatedImages/rocket_gen_img_1161a7ebd-1763294739154.png'
                    }
                    alt={getPhotographerDisplayName(booking)}
                    className="w-20 h-20 rounded-xl object-cover"
                  />
                </div>

                {/* Booking Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-1">
                        {getPhotographerDisplayName(booking)}
                      </h3>
                      <p className="text-slate-600">{booking.service_type || booking.serviceType}</p>
                    </div>
                    {booking.status === 'cancelled_by_photographer' && (booking.payment_status ?? booking.paymentStatus) === 'refunded'
                      ? getStatusBadge('refunded')
                      : getStatusBadge(booking.status)}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Icon name="Calendar" size={18} />
                      <span>
                        {booking.booking_date || booking.bookingDate || booking.scheduledAt
                          ? new Date(booking.booking_date || booking.bookingDate || booking.scheduledAt).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          : 'غير محدد'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Icon name="Clock" size={18} />
                      <span>{formatTimeToArabic(booking.booking_time || booking.bookingTime)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Icon name="MapPin" size={18} />
                      <span>{getCityLabel(booking.city) || booking.city || 'الرياض'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <Icon name="Wallet" size={18} className="text-primary" />
                      <span className="font-semibold">{formatNumber(booking.finalPrice || booking.final_price_sar || booking.price || 0)} ر.س</span>
                    </div>
                  </div>

                  {/* Additional Booking Info */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4 text-xs text-slate-600">
                    {booking.photography_type && (
                      <div className="flex items-center gap-2">
                        <Icon name="Camera" size={14} />
                        <span>{getPhotographyTypeLabelAr(booking.photography_type)}</span>
                      </div>
                    )}
                    {booking.duration && (
                      <div className="flex items-center gap-2">
                        <Icon name="Timer" size={14} />
                        <span>{formatDurationAr(booking.duration)}</span>
                      </div>
                    )}
                    {booking.location && (
                      <div className="flex items-center gap-2 md:col-span-1 col-span-2">
                        <Icon name="MapPin" size={14} />
                        <span className="truncate">{booking.location}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    {/* Show "عرض التفاصيل" button only for bookings that don't have a specific action button */}
                    {!(['paid_confirmed', 'session_started', 'awaiting_delivery', 'completed'].includes(booking.status)) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedBooking(booking);
                          setShowDetails(true);
                        }}
                        iconName="Eye"
                      >
                        عرض التفاصيل
                      </Button>
                    )}
                    {booking.status === 'session_started' && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-xl">
                        <span className="text-purple-600 text-base">🎬</span>
                        <div>
                          <p className="text-xs font-semibold text-purple-800 leading-none">الجلسة جارية الآن</p>
                          {booking.sessionPin && (
                            <p className="text-[11px] text-purple-600 mt-0.5">
                              رمز التحقق: <strong className="tracking-widest text-sm text-purple-900">{booking.sessionPin}</strong>
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {booking.status === 'awaiting_delivery' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedBooking(booking);
                          setShowDetails(true);
                        }}
                        iconName="Clock"
                        className="text-orange-700 border-orange-300 hover:bg-orange-50"
                      >
                        المصور يجهّز الصور
                      </Button>
                    )}
                    {(booking.status === 'pending_photographer_confirmation' || booking.status === 'pending' || booking.status === 'awaiting_payment') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          try {
                            if (booking.status === 'awaiting_payment') {
                              navigate(`/booking-payment/${booking.id}`);
                            } else {
                              navigate(`/booking-waiting/${booking.id}`);
                            }
                          } catch (error) {
                            console.error('Navigation error:', error);
                            alert('حدث خطأ أثناء الانتقال. يرجى المحاولة مرة أخرى.');
                          }
                        }}
                        iconName="Clock"
                      >
                        {booking.status === 'awaiting_payment' ? 'إتمام الدفع' : 'متابعة الحالة'}
                      </Button>
                    )}
                    {(booking.status === 'pending_photographer_confirmation' || booking.status === 'pending' || booking.status === 'awaiting_payment') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancelBooking(booking.id)}
                        disabled={cancelling}
                        loading={cancelling && selectedBooking?.id === booking.id}
                        className="text-error border-error hover:bg-error/10"
                        iconName="X"
                      >
                        إلغاء الطلب
                      </Button>
                    )}
                    {booking.status === 'completed' && (
                      <div className="w-full flex flex-col gap-2">
                        {Capacitor.isNativePlatform() ? (
                          /* تطبيق الجوال: ينقل لصفحة التسليمات مباشرة */
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/customer-dashboard/my-deliveries', { state: { bookingId: booking.id } })}
                            iconName="GalleryHorizontalEnd"
                            iconPosition="left"
                            className="w-full bg-slate-900 hover:bg-slate-800 text-white border-slate-900"
                          >
                            عرض وتحميل الصور
                          </Button>
                        ) : (
                          /* ويب: الطريقة الأصلية برابط خارجي */
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadFiles(booking.id)}
                              disabled={downloadingFilesId === booking.id}
                              loading={downloadingFilesId === booking.id}
                              iconName="Download"
                              iconPosition="left"
                              className="w-full bg-slate-800 hover:bg-slate-900 text-white border-slate-800 hover:border-slate-900"
                            >
                              {downloadingFilesId === booking.id ? 'جاري التحضير...' : 'تحميل الملفات'}
                            </Button>
                            {reviewBookingId === booking.id ? (
                              <div className="w-full bg-amber-50 border border-amber-200 rounded-xl p-3 flex flex-col gap-2">
                                {loadingReview ? (
                                  <p className="text-center text-sm text-slate-500">جاري التحميل...</p>
                                ) : existingReview ? (
                                  <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2 text-green-700">
                                      <Icon name="CheckCircle" size={16} className="text-green-600" />
                                      <p className="text-sm font-semibold">تم إرسال تقييمك</p>
                                    </div>
                                    <div className="flex justify-center">
                                      <StarRating rating={existingReview.rating} size={26} interactive={false} />
                                    </div>
                                    {existingReview.comment && (
                                      <p className="text-xs text-slate-600 bg-white border border-slate-200 rounded-lg p-2" dir="rtl">
                                        {existingReview.comment}
                                      </p>
                                    )}
                                    <button onClick={closeReview} className="text-xs text-slate-400 hover:text-slate-600 text-center transition-colors">
                                      إغلاق
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <p className="text-sm font-bold text-slate-700 text-center">⭐ قيّم المصور</p>
                                    <div className="flex justify-center">
                                      <StarRating rating={reviewRating} onRate={setReviewRating} size={28} />
                                    </div>
                                    <textarea
                                      rows={2}
                                      value={reviewComment}
                                      onChange={(e) => setReviewComment(e.target.value)}
                                      placeholder="اكتب تعليقك هنا (اختياري)"
                                      className="w-full text-sm border border-slate-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-amber-300"
                                      dir="rtl"
                                    />
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleSubmitReview(booking)}
                                        disabled={!reviewRating || submittingReview}
                                        className="flex-1 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold disabled:opacity-50 transition-colors"
                                      >
                                        {submittingReview ? '...' : 'إرسال التقييم'}
                                      </button>
                                      <button
                                        onClick={closeReview}
                                        className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                                      >
                                        إلغاء
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() => openReview(booking)}
                                className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 text-sm font-semibold transition-colors"
                              >
                                <Icon name="Star" size={16} className="text-amber-500 fill-amber-400" />
                                قيّم المصور
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {/* رمز PIN — يظهر عندما يبدأ المصور الجلسة وينتظر الرمز من العميل */}
                    {booking.status === 'paid_confirmed' && booking.sessionPin && (
                      <div className="w-full space-y-2">
                        <div className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-purple-50 border-2 border-purple-300 rounded-xl animate-pulse">
                          <div>
                            <p className="text-xs font-semibold text-purple-700">📸 المصور وصل — أعطِه هذا الرمز:</p>
                            <p className="text-3xl font-black tracking-[0.4em] text-purple-900 mt-0.5">{booking.sessionPin}</p>
                          </div>
                          <span className="text-4xl">🔐</span>
                        </div>
                        {/* زر الإبلاغ — أسفل الـ PIN */}
                        <NoShowButton
                          booking={booking}
                          alreadyReported={!!(booking.no_show_reported_at || noShowReportedIds.has(booking.id))}
                          reporting={reportingNoShowId === booking.id}
                          onReport={() => handleReportNoShow(booking)}
                        />
                      </div>
                    )}
                    {booking.status === 'paid_confirmed' && !booking.sessionPin && (() => {
                      // حساب ما إذا بدأت الجلسة
                      const rawDate = booking.booking_date || booking.bookingDate;
                      const rawTime = booking.booking_time || booking.bookingTime || '00:00';
                      const sessionDateTime = rawDate ? new Date(rawDate) : null;
                      if (sessionDateTime) {
                        const [h, m] = String(rawTime).split(':').map(Number);
                        sessionDateTime.setHours(h || 0, m || 0, 0, 0);
                      }
                      const sessionStarted = sessionDateTime && sessionDateTime <= new Date();

                      return (
                        <div className="w-full space-y-2">
                          {/* الأزرار العادية — تظهر دائماً قبل بدء الجلسة */}
                          {!sessionStarted && (
                            <div className="flex gap-2 flex-wrap">
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => {
                                  try { navigate(`/booking-confirmation?id=${booking.id}`); }
                                  catch (error) { console.error('Navigation error:', error); }
                                }}
                                iconName="CheckCircle"
                              >
                                عرض صفحة الحجز
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openCancelRequest(booking)}
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                iconName="XCircle"
                              >
                                طلب الإلغاء
                              </Button>
                            </div>
                          )}
                          {/* زر الإبلاغ — يظهر من بداية الجلسة (رصاصي مع عداد → أحمر بعد 60 د) */}
                          <NoShowButton
                            booking={booking}
                            alreadyReported={!!(booking.no_show_reported_at || noShowReportedIds.has(booking.id))}
                            reporting={reportingNoShowId === booking.id}
                            onReport={() => handleReportNoShow(booking)}
                          />
                        </div>
                      );
                    })()}
                    {booking.status === 'cancellation_requested' && (
                      <div className="w-full flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-xl">
                        <Icon name="Clock" size={15} className="text-orange-500 shrink-0" />
                        <p className="text-xs text-orange-700 font-medium">طلب الإلغاء قيد المراجعة من الإدارة</p>
                      </div>
                    )}
                    {booking.status === 'cancelled_by_photographer' && (
                      <PhotographerWithdrewActions
                        booking={booking}
                        refundSubmitting={refundSubmitting}
                        refundDoneId={refundDoneId}
                        onFindAnotherPhotographer={() => navigate(`/booking-waiting/${booking.id}`)}
                        onRequestRefund={() => handleRequestRefund(booking.id)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
          <Icon name="Calendar" size={64} className="mx-auto mb-4 text-slate-400" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">لا توجد حجوزات</h3>
          <p className="text-slate-600 mb-6">
            {activeTab === 'all'
              ? 'لم تقم بأي حجز بعد. ابدأ بحجز جديد الآن!'
              : `لا توجد حجوزات بحالة "${tabs.find((t) => t.id === activeTab)?.label}"`}
          </p>
          <Button
            variant="default"
            onClick={() => navigate('/booking-wizard')}
            iconName="Plus"
            iconPosition="right"
          >
            حجز جديد
          </Button>
        </div>
      )}

      {/* Booking Details Modal */}
      {showDetails && selectedBooking && (
        <BookingDetailsModal
          booking={selectedBooking}
          onClose={() => {
            setShowDetails(false);
            setSelectedBooking(null);
          }}
          onCancel={handleCancelBooking}
          getStatusBadge={getStatusBadge}
          onDownloadFiles={handleDownloadFiles}
          downloadingFilesId={downloadingFilesId}
          onRequestRefund={handleRequestRefund}
          refundSubmitting={refundSubmitting}
          refundDoneId={refundDoneId}
          onFindAnotherPhotographer={(bookingId) => {
            setShowDetails(false);
            setSelectedBooking(null);
            navigate(`/booking-waiting/${bookingId}`);
          }}
        />
      )}

      {/* ── Cancellation Request Modal ── */}
      {cancelRequestBooking && cancelPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">طلب إلغاء الحجز</h3>
              <button onClick={() => setCancelRequestBooking(null)} className="text-slate-400 hover:text-slate-600 transition">
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Fee breakdown */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 space-y-2">
              <p className="text-xs font-semibold text-amber-700 mb-1">رسوم الإلغاء (حسب الوقت المتبقي قبل الجلسة)</p>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">رسوم الإلغاء</span>
                <span className="font-bold text-red-600">{cancelPreview.feePct}% ({formatNumber(cancelPreview.feeAmount)} ر.س)</span>
              </div>
              <div className="flex justify-between text-sm border-t border-amber-200 pt-2">
                <span className="text-slate-600">المبلغ المُسترد</span>
                <span className="font-bold text-green-700">{cancelPreview.refundPct}% ({formatNumber(cancelPreview.refundAmount)} ر.س)</span>
              </div>
              <p className="text-[11px] text-amber-600 mt-1">⏱ رد المبلغ خلال 3-5 أيام عمل بعد موافقة الإدارة</p>
            </div>

            {/* Fee tiers reference */}
            <div className="text-[11px] text-slate-500 bg-slate-50 rounded-lg p-3 mb-4 space-y-1">
              <p className="font-semibold text-slate-600 mb-1">جدول الرسوم:</p>
              <p>• أكثر من 48 ساعة → رسوم 10%</p>
              <p>• 24–48 ساعة → رسوم 30%</p>
              <p>• أقل من 24 ساعة → رسوم 50%</p>
            </div>

            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="سبب الإلغاء (اختياري)..."
              rows={3}
              className="w-full border border-slate-200 focus:border-primary rounded-xl px-3 py-2 text-sm resize-none focus:outline-none mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={() => setCancelRequestBooking(null)}
                className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition text-sm"
              >
                تراجع
              </button>
              <button
                onClick={handleSubmitCancelRequest}
                disabled={cancelSubmitting}
                className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold transition text-sm flex items-center justify-center gap-2"
              >
                {cancelSubmitting
                  ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الإرسال...</>
                  : 'تأكيد طلب الإلغاء'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery URL Modal */}
      {deliveryUrlModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Icon name="CheckCircle2" size={24} className="text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">الرابط جاهز!</h3>
                <p className="text-xs text-slate-500">صلاحيته 7 أيام</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              اضغط الزر أدناه لفتح صفحة استلام صورك في المتصفح. ستحتاج إلى التحقق برقم جوالك.
            </p>
            <a
              href={deliveryUrlModal}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full text-center bg-primary text-white rounded-xl py-3.5 font-bold mb-3 active:opacity-80 transition-opacity"
              onClick={() => setTimeout(() => setDeliveryUrlModal(null), 500)}
            >
              <Icon name="ExternalLink" size={18} className="text-white" />
              فتح صفحة الاستلام في المتصفح
            </a>
            <button
              onClick={() => setDeliveryUrlModal(null)}
              className="block w-full text-center text-slate-400 text-sm py-2"
            >
              إغلاق
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Booking Details Modal Component
const BookingDetailsModal = ({ booking, onClose, onCancel, getStatusBadge, onDownloadFiles, downloadingFilesId, onRequestRefund, refundSubmitting, refundDoneId, onFindAnotherPhotographer }) => {
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);

  // إخفاء القائمة السفلية أثناء فتح الموديل
  useEffect(() => {
    document.body.classList.add('ticket-modal-open');
    return () => document.body.classList.remove('ticket-modal-open');
  }, []);
  
  // Use the same formatTimeToArabic function
  const formatTimeToArabic = (time) => {
    if (!time) return 'غير محدد';
    if (typeof time === 'string' && (time.includes('ص') || time.includes('م'))) {
      return time;
    }
    const timeStr = String(time);
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const min = minutes || '00';
    if (isNaN(hour)) return time;
    if (hour === 0) {
      return `12:${min} ص`;
    } else if (hour < 12) {
      return `${hour}:${min} ص`;
    } else if (hour === 12) {
      return `12:${min} م`;
    } else {
      const displayHour = hour - 12;
      return `${displayHour}:${min} م`;
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-slate-900">تفاصيل الحجز</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Photographer Info */}
          <div className="flex items-center gap-4 pb-6 border-b border-slate-200">
            <Image
              src={
                booking.photographer?.avatar_url ||
                booking.photographer?.avatarUrl ||
                'https://img.rocket.new/generatedImages/rocket_gen_img_1161a7ebd-1763294739154.png'
              }
              alt={getPhotographerDisplayName(booking)}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                {getPhotographerDisplayName(booking)}
              </h3>
              <p className="text-slate-600">{booking.service_type || booking.serviceType}</p>
            </div>
          </div>

          {/* Booking Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-600 mb-1">رقم الحجز</p>
              <p className="font-mono font-medium text-primary">
                #{String(booking.id).slice(0, 8).toUpperCase()}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">الحالة</p>
              {getStatusBadge(booking.status)}
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">التاريخ</p>
              <p className="font-medium text-slate-900">
                {booking.booking_date || booking.bookingDate || booking.scheduledAt
                  ? (() => {
                      const date = new Date(booking.booking_date || booking.bookingDate || booking.scheduledAt);
                      const days = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
                      const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
                      const dayName = days[date.getDay()];
                      const day = date.getDate();
                      const month = months[date.getMonth()];
                      const year = date.getFullYear();
                      return `${dayName}، ${day} ${month} ${year}`;
                    })()
                  : 'غير محدد'}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">الوقت</p>
              <p className="font-medium text-slate-900">{formatTimeToArabic(booking.booking_time || booking.bookingTime)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">نوع التصوير</p>
              <p className="font-medium text-slate-900">{getPhotographyTypeLabelAr(booking.photography_type || booking.photographyType || booking.service_type || booking.serviceType)}</p>
            </div>
            {(() => {
              const snap = booking.pricingSnapshot || booking.pricing_snapshot || {};
              const methods = snap.shootingMethods || (booking.shooting_method || booking.shootingMethod ? [booking.shooting_method || booking.shootingMethod] : []);
              if (!methods.length) return null;
              const labels = methods.map(m => getShootingMethodLabelAr(m)).filter(Boolean).join('، ');
              return (
                <div>
                  <p className="text-sm text-slate-600 mb-1">وسيلة التصوير</p>
                  <p className="font-medium text-slate-900">{labels}</p>
                </div>
              );
            })()}
            {(booking.shooting_place_type || booking.shootingPlaceType ||
              (booking.pricingSnapshot || booking.pricing_snapshot || {})?.shootingPlaceType) ? (
              <div>
                <p className="text-sm text-slate-600 mb-1">مكان التصوير</p>
                <p className="font-medium text-slate-900">
                  {getShootingPlaceTypeLabelAr(
                    booking.shooting_place_type ||
                    booking.shootingPlaceType ||
                    (booking.pricingSnapshot || booking.pricing_snapshot || {})?.shootingPlaceType
                  )}
                </p>
              </div>
            ) : null}
            {booking.workload_bucket || booking.workloadBucket ? (
              <div>
                <p className="text-sm text-slate-600 mb-1">حجم العمل</p>
                <p className="font-medium text-slate-900">
                  {getWorkloadLabelAr(
                    booking.photography_type || booking.photographyType || booking.service_type || booking.serviceType,
                    booking.workload_bucket || booking.workloadBucket
                  )}
                </p>
              </div>
            ) : null}
            {(() => {
              const snap = booking.pricingSnapshot || booking.pricing_snapshot || {};
              const pt =
                booking.photography_type ||
                booking.photographyType ||
                booking.service_type ||
                booking.serviceType;
              const ps =
                booking.product_physical_size_bucket ||
                booking.productPhysicalSizeBucket ||
                snap.productPhysicalSizeBucket ||
                snap.product_physical_size_bucket;
              if (pt !== 'product' || !ps) return null;
              return (
                <div>
                  <p className="text-sm text-slate-600 mb-1">حجم المنتجات</p>
                  <p className="font-medium text-slate-900 text-pretty">{getProductPhysicalSizeLabelAr(ps)}</p>
                </div>
              );
            })()}
            {booking.delivered_photos_bucket || booking.deliveredPhotosBucket ? (
              <div>
                <p className="text-sm text-slate-600 mb-1">الصور المسلّمة</p>
                <p className="font-medium text-slate-900">
                  {getDeliveredPhotosLabelAr(booking.delivered_photos_bucket || booking.deliveredPhotosBucket)}
                </p>
              </div>
            ) : null}
            {(() => {
              const snap = booking.pricingSnapshot || booking.pricing_snapshot || {};
              const photosCount = snap.photosCount ?? snap.photos_count;
              const videosCount = snap.videosCount ?? snap.videos_count;
              const shortVideosCount = snap.shortVideosCount ?? snap.short_videos_count;
              const deliverableLabel = snap.deliverableLabel ?? snap.deliverable_label;
              const hasDeliverables = snap.deliverableMode !== 'unspecified' && (photosCount > 0 || videosCount > 0 || shortVideosCount > 0 || deliverableLabel);
              if (!hasDeliverables) return null;
              const label = deliverableLabel || [
                photosCount > 0 ? `${photosCount} صورة` : null,
                videosCount > 0 ? `${videosCount} فيديو طويل` : null,
                shortVideosCount > 0 ? `${shortVideosCount} فيديو قصير` : null,
              ].filter(Boolean).join(' + ');
              return (
                <div>
                  <p className="text-sm text-slate-600 mb-1">عدد المخرجات المطلوبة</p>
                  <p className="font-medium text-slate-900">{label}</p>
                </div>
              );
            })()}
            <div>
              <p className="text-sm text-slate-600 mb-1">المدينة</p>
              <p className="font-medium text-slate-900">{getCityLabel(booking.city) || booking.city || 'الرياض'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">المدة</p>
              <p className="font-medium text-slate-900">{formatDurationAr(booking.duration)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">الموقع</p>
              <p className="font-medium text-slate-900">{booking.location || 'غير محدد'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">السعر الإجمالي</p>
              <p className="font-bold text-lg text-primary">{formatNumber(booking.finalPrice || booking.final_price_sar || booking.price || 0)} ر.س</p>
            </div>
            {booking.payment_transaction_id && (
              <div>
                <p className="text-sm text-slate-600 mb-1">رقم المعاملة</p>
                <p className="font-mono text-xs text-slate-900">{booking.payment_transaction_id}</p>
              </div>
            )}
          </div>


          {booking.notes && (
            <div>
              <p className="text-sm text-slate-600 mb-2">ملاحظات</p>
              <p className="text-slate-900 bg-slate-50 p-4 rounded-lg">{booking.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-200 flex-wrap">
            {(booking.status === 'pending_photographer_confirmation' || booking.status === 'pending' || booking.status === 'awaiting_payment') && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    setCancelling(true);
                    await onCancel(booking.id);
                    onClose();
                  } catch (error) {
                    console.error('Cancel error:', error);
                  } finally {
                    setCancelling(false);
                  }
                }}
                disabled={cancelling}
                loading={cancelling}
                className="text-error border-error hover:bg-error/10"
                iconName="X"
              >
                إلغاء الطلب
              </Button>
            )}
            {booking.status === 'awaiting_payment' && (
              <Button
                variant="default"
                onClick={() => {
                  try {
                    onClose();
                    navigate(`/booking-payment/${booking.id}`);
                  } catch (error) {
                    console.error('Navigation error:', error);
                    alert('حدث خطأ أثناء الانتقال. يرجى المحاولة مرة أخرى.');
                  }
                }}
                iconName="CreditCard"
                iconPosition="right"
                className="flex-1"
              >
                إتمام الدفع
              </Button>
            )}
            {(booking.status === 'pending_photographer_confirmation' || booking.status === 'pending') && (
              <Button
                variant="default"
                onClick={() => {
                  try {
                    onClose();
                    navigate(`/booking-waiting/${booking.id}`);
                  } catch (error) {
                    console.error('Navigation error:', error);
                    alert('حدث خطأ أثناء الانتقال. يرجى المحاولة مرة أخرى.');
                  }
                }}
                iconName="Clock"
                iconPosition="right"
                className="flex-1"
              >
                متابعة الحالة
              </Button>
            )}
            {booking.status === 'paid_confirmed' && (
              <Button
                variant="outline"
                onClick={() => {
                  onClose();
                  openCancelRequest(booking);
                }}
                className="text-red-600 border-red-300 hover:bg-red-50"
                iconName="XCircle"
              >
                طلب الإلغاء
              </Button>
            )}
            {booking.status === 'paid_confirmed' && (
              <Button
                variant="default"
                onClick={() => {
                  try {
                    onClose();
                    navigate(`/booking-confirmation?id=${booking.id}`);
                  } catch (error) {
                    console.error('Navigation error:', error);
                    alert('حدث خطأ أثناء الانتقال. يرجى المحاولة مرة أخرى.');
                  }
                }}
                iconName="CheckCircle"
                iconPosition="right"
                className="flex-1"
              >
                عرض تفاصيل الحجز
              </Button>
            )}
            {booking.status === 'session_started' && (
              <div className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-purple-50 border border-purple-200 rounded-xl">
                <span className="text-purple-600 text-lg animate-pulse">🎬</span>
                <div className="text-center">
                  <p className="text-sm font-bold text-purple-800">الجلسة جارية الآن</p>
                  {booking.sessionPin && (
                    <p className="text-xs text-purple-600">
                      رمز جلستك: <strong className="tracking-widest text-purple-900">{booking.sessionPin}</strong>
                    </p>
                  )}
                </div>
              </div>
            )}
            {booking.status === 'completed' && (
              <div className="flex-1 flex flex-col gap-2">
                {Capacitor.isNativePlatform() ? (
                  /* تطبيق الجوال: ينقل لصفحة التسليمات مباشرة */
                  <Button
                    variant="default"
                    onClick={() => { onClose(); navigate('/customer-dashboard/my-deliveries', { state: { bookingId: booking.id } }); }}
                    iconName="GalleryHorizontalEnd"
                    iconPosition="left"
                    className="w-full bg-slate-900 hover:bg-slate-800 border-slate-900"
                  >
                    عرض وتحميل الصور
                  </Button>
                ) : (
                  /* ويب: الطريقة الأصلية */
                  <>
                    <Button
                      variant="default"
                      onClick={() => onDownloadFiles?.(booking.id)}
                      disabled={downloadingFilesId === booking.id}
                      loading={downloadingFilesId === booking.id}
                      iconName="Download"
                      iconPosition="left"
                      className="w-full bg-slate-800 hover:bg-slate-900 border-slate-800 hover:border-slate-900"
                    >
                      {downloadingFilesId === booking.id ? 'جاري التحضير...' : 'تحميل الملفات'}
                    </Button>
                    {reviewBookingId === booking.id ? (
                      <div className="w-full bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col gap-3">
                        {loadingReview ? (
                          <p className="text-center text-sm text-slate-500">جاري التحميل...</p>
                        ) : existingReview ? (
                          <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-xl">
                              <Icon name="CheckCircle" size={18} className="text-green-600 flex-shrink-0" />
                              <p className="text-sm font-semibold text-green-800">تم إرسال تقييمك بنجاح</p>
                            </div>
                            <div className="flex justify-center">
                              <StarRating rating={existingReview.rating} size={32} interactive={false} />
                            </div>
                            {existingReview.comment && (
                              <p className="text-sm text-slate-600 bg-white border border-slate-200 rounded-xl p-3 leading-relaxed" dir="rtl">
                                {existingReview.comment}
                              </p>
                            )}
                            <button onClick={closeReview} className="text-xs text-slate-400 hover:text-slate-600 text-center transition-colors">
                              إغلاق
                            </button>
                          </div>
                        ) : (
                          <>
                            <p className="text-base font-bold text-slate-700 text-center">⭐ قيّم المصور</p>
                            <div className="flex justify-center">
                              <StarRating rating={reviewRating} onRate={setReviewRating} size={32} />
                            </div>
                            <textarea
                              rows={3}
                              value={reviewComment}
                              onChange={(e) => setReviewComment(e.target.value)}
                              placeholder="شاركنا تجربتك مع المصور (اختياري)"
                              className="w-full text-sm border border-slate-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-amber-300"
                              dir="rtl"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSubmitReview(booking)}
                                disabled={!reviewRating || submittingReview}
                                className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold disabled:opacity-50 transition-colors"
                              >
                                {submittingReview ? 'جاري الإرسال...' : 'إرسال التقييم'}
                              </button>
                              <button
                                onClick={closeReview}
                                className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                              >
                                إلغاء
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => openReview(booking)}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 text-sm font-semibold transition-colors"
                      >
                        <Icon name="Star" size={18} className="text-amber-500 fill-amber-400" />
                        قيّم المصور
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            {booking.status === 'cancelled_by_photographer' && (
              <PhotographerWithdrewActions
                booking={booking}
                refundSubmitting={refundSubmitting}
                refundDoneId={refundDoneId}
                onFindAnotherPhotographer={() => {
                  onClose();
                  onFindAnotherPhotographer?.(booking.id);
                }}
                onRequestRefund={() => {
                  onClose();
                  onRequestRefund?.(booking.id);
                }}
              />
            )}
            <Button variant="outline" onClick={onClose} className="flex-1">
              إغلاق
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Photographer Withdrew Actions ───────────────────────────────────────────
const PhotographerWithdrewActions = ({ booking, refundSubmitting, refundDoneId, onFindAnotherPhotographer, onRequestRefund }) => {
  // getMyBookings returns camelCase via toCamelCase(), so fallback to camelCase variant
  const ps = booking.payment_status ?? booking.paymentStatus;
  const refundDone = ps === 'refunded';
  const refundRequested = ps === 'refund_pending' || refundDoneId === booking.id;

  // Refund completed — show success banner only
  if (refundDone) {
    return (
      <div className="w-full flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl" dir="rtl">
        <span className="text-2xl">✅</span>
        <div>
          <p className="text-sm font-bold text-green-800">تم استرداد مبلغك</p>
          <p className="text-xs text-green-600 mt-0.5">أُعيد المبلغ كاملاً — قد يستغرق ظهوره 3-5 أيام عمل.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-3 p-3 bg-red-50 border border-red-200 rounded-xl" dir="rtl">
      <div className="flex items-start gap-2">
        <span className="text-xl">⚠️</span>
        <div>
          <p className="text-sm font-bold text-red-800">اعتذر المصور عن الحضور</p>
          <p className="text-xs text-red-600 mt-0.5">اختر أحد الخيارين أدناه:</p>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {!refundRequested && (
          <button
            onClick={onFindAnotherPhotographer}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 active:opacity-80 transition"
          >
            <span>🔍</span>
            ابحث عن مصور آخر
          </button>
        )}
        {refundRequested ? (
          <div className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-green-100 border border-green-300 text-green-800 text-sm font-semibold">
            <span>✅</span>
            طلب الاسترداد مُقدَّم — سيتم الاسترداد خلال 1-3 أيام عمل
          </div>
        ) : (
          <button
            onClick={onRequestRefund}
            disabled={refundSubmitting}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-red-300 bg-white text-red-700 text-sm font-bold hover:bg-red-50 active:opacity-80 transition disabled:opacity-50"
          >
            {refundSubmitting ? '...' : (<><span>💰</span> استرداد المبلغ</>)}
          </button>
        )}
      </div>
    </div>
  );
};

export default Bookings;
